import React, { FC, useEffect, useState } from 'react'
import useFormData from 'hooks/formData'
import { DefaultCollectionTypes, SaatchiLatestCollectionTypes } from 'components/Collection/Data'
import { CollectionFormDefaults, AA_SELECTION_TYPE } from './constants'
import { formValidationRules } from './validation'
import CloseIcon from 'svg/SA/close-small.svg'
import EyeOffIcon from 'svg/SA/eye-off-small.svg'
import EyeOnIcon from 'svg/SA/eye-small.svg'
import styles from './Form.module.scss'
import classNames from 'classnames'
import InputLabel from 'components/SA/FormElements/InputLabel'
import TextAreaLabel from 'components/SA/FormElements/TextareaLabel'
import Checkbox from 'components/SA/FormElements/Checkbox'
import FormRadio from 'components/SA/FormElements/Radio'
import Toggle from 'components/SA/FormElements/Toggle'

type Props = {
  onCollectionSubmit?: (formData: Record<string, any>) => void
  isAdmin: boolean
  isArtist: boolean
  isSubmitting: boolean
  closeModal: () => void
  collectionData?: Record<string, any>
  closeIcon?: boolean
}

const CreateCollectionV2: FC<Props> = (props) => {
  const {
    onCollectionSubmit,
    isAdmin,
    isArtist,
    isSubmitting,
    closeModal,
    collectionData,
    closeIcon = true,
  } = props

  const [noValidate, setNoValidate] = useState(false)

  // Initialize form with existing collection data if provided, otherwise use defaults
  const initialFormData = collectionData ? {
    ...CollectionFormDefaults,
    ...collectionData,
    isSetup: true,
  } : { ...CollectionFormDefaults, isSetup: true }

  const {
    formData,
    formErrors,
    updateFormData,
    onChangeInput,
    onChangeCheckbox,
    validateFormData,
  } = useFormData({
    initialState: initialFormData,
    formRules: formValidationRules,
  })
  const {
    collectionTypeIds,
    description,
    email,
    firstName,
    lastName,
    privacy,
    title,
    visible,
    zendeskTicketId,
    printsView,
  } = formData

  const handleCloseModal = () => {
    setNoValidate(true)
    closeModal()
  }

  const handleChangePrivacyFormValue = () => {
    updateFormData({
      ['privacy']: privacy === '1' ? '0' : '1',
    })
  }

  const handleChangePrintsViewFormValue = () => {
    updateFormData({
      ['printsView']: printsView === '1' ? '0' : '1',
    })
  }

  const isAACollection = collectionTypeIds.includes(AA_SELECTION_TYPE)
  const collectionIdErrors = formErrors.collectionTypeIds

  const onSubmit = async (ev: React.SyntheticEvent<HTMLElement>) => {
    ev.preventDefault()

    // We will use a hook to track local data VS lots of updates in Epics/Redux
    // When we are finally ready to SUBMIT we will UPDATE via Epics/Redux
    // We use the Redux FormData in Epics to validate and track Errors
    if (!noValidate) {
      const isValid = await validateFormData()

      if (isValid) {
        onCollectionSubmit && onCollectionSubmit(formData)
      }
    }
  }

  const typesCheckboxOptionsArray = isAdmin
    ? Object.keys(SaatchiLatestCollectionTypes)
    : Object.keys(DefaultCollectionTypes)

  useEffect(() => {
    updateFormData({ ...initialFormData })
  }, [])

  useEffect(() => {
    if (!collectionData) {
      updateFormData({
        visible: isAACollection ? '0' : '1',
      })
    }
  }, [isAACollection, collectionData])

  useEffect(() => {
    // Only set default collection types if we're creating a new collection (no collectionData)
    if (!collectionData) {
      updateFormData({
      // Only set the default if it's not a collector or artist
      // The rest of the form is not used for other user types
      // This way we make sure new user types are able to create collections
      ['collectionTypeIds']: !isAdmin && !isArtist ? ['1'] : [],
    })
    }
  }, [isAdmin, isArtist, collectionData])

  // Make sure we have Initial Payload/FormData `isSetup`
  if (!formData.isSetup) {
    return null
  }

  return (
    <>
      <div className={styles.createFormHeader}>
        <h4>{collectionData ? 'Edit Collection' : 'Create A Collection'}</h4>
        {closeIcon && (
          <button onClick={handleCloseModal}>
            <CloseIcon />
          </button>
        )}
      </div>
      <form
        id='createCollection'
        name='createCollection'
        autoComplete='off'
        className={styles.formContainer}
      >
        <div className={styles.infoAndOptionsContainer}>
          <div className={styles.collectionInfoDiv}>
            <div className={styles.inputTitleBox}>
              <h5>Collection Title</h5>
              <InputLabel
                name='title'
                maxLength={65}
                value={title}
                title=''
                onChange={onChangeInput}
                error={formErrors.title}
                styleOverride={styles.fullWidth}
                isMaxLengthVisible={true}
              />
            </div>

            <div className={styles.inputTitleBox}>
              <h5>Collection Description</h5>
              <TextAreaLabel
                name='description'
                maxLength={200}
                value={description}
                title=''
                onChange={onChangeInput}
                error={formErrors.description}
                styleOverride={styles.fullWidth}
              />
            </div>

            <div className={styles.toggleMessageSection}>
              <div className={styles.toggleDiv}>
                <h5>Private</h5>
                <div>
                  <Toggle value={privacy === '1'} onToggle={handleChangePrivacyFormValue} />
                  {privacy === '1' ? <EyeOffIcon /> : <EyeOnIcon />}
                </div>
              </div>
              <p>
                {privacy === '1'
                  ? 'This collection will not be visible to others'
                  : 'This collection may be visible to others'}
              </p>
            </div>
          </div>

          {(isAdmin || isArtist) && (
            <div className={styles.collectionOptionsDiv}>
              <h5>Saatchi Art</h5>
              <div className={styles.checkboxContainer}>
                {typesCheckboxOptionsArray.map((key) => (
                  <Checkbox
                    key={key}
                    size='large'
                    customLabelStyle={styles.customCheckboxLabelStyle}
                    name='collectionTypeIds'
                    checked={collectionTypeIds.includes(key)}
                    value={key}
                    onClick={onChangeCheckbox}
                  >
                    <p>{SaatchiLatestCollectionTypes[key]}</p>
                  </Checkbox>
                ))}
              </div>
              {Boolean(collectionIdErrors) && (
                <p
                  className='defaultTypographyXSmall'
                  style={{ color: '#da3b1f' }}
                  data-type='error'
                >
                  {collectionIdErrors}
                </p>
              )}

              {isAACollection && (
                <div className={styles.aaFormWrapper}>
                  <h5>Client Information</h5>
                  <div className={styles.aaFormContainer}>
                    <div className={styles.aaFormFieldBox}>
                      <div className={styles.aaFormFieldTitle}>
                        <p>First Name</p>
                        <InputLabel
                          name='firstName'
                          value={firstName}
                          title=''
                          onChange={onChangeInput}
                          error={formErrors.firstName}
                          styleOverride={styles.fullWidth}
                        />
                      </div>
                      <div className={styles.aaFormFieldTitle}>
                        <p>Last Name</p>
                        <InputLabel
                          name='lastName'
                          value={lastName}
                          title=''
                          onChange={onChangeInput}
                          error={formErrors.lastName}
                          styleOverride={styles.fullWidth}
                        />
                      </div>
                    </div>

                    <div className={styles.aaFormFieldBox}>
                      <div className={styles.aaFormFieldTitle}>
                        <p>Email</p>
                        <InputLabel
                          name='email'
                          value={email}
                          title=''
                          onChange={onChangeInput}
                          error={formErrors.email}
                          styleOverride={styles.fullWidth}
                        />
                      </div>
                      <div className={styles.aaFormFieldTitle}>
                        <p>Zendesk Ticket ID</p>
                        <InputLabel
                          name='zendeskTicketId'
                          value={zendeskTicketId}
                          title=''
                          onChange={onChangeInput}
                          error={formErrors.zendeskTicketId}
                          styleOverride={styles.fullWidth}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isAdmin && privacy === '0' && (
          <div className={styles.visibilityPrints}>
            <>
              <h5>Visibility</h5>
              <div className={styles.radioContainer}>
                <FormRadio
                  size='large'
                  name='visible'
                  value='1'
                  text='Browsable'
                  onClick={onChangeInput}
                  checked={visible === '1'}
                  aria-label='Select Browsable'
                />

                <FormRadio
                  size='large'
                  name='visible'
                  value='0'
                  text='Accessible by URL only'
                  onClick={onChangeInput}
                  checked={visible === '0'}
                  aria-label='Select Accessible by URL only'
                  customStyles={styles.visibilityRadioCustomStyle}
                />
              </div>
            </>
          </div>
        )}

        {isAdmin && (
          <div className={classNames(styles.toggleMessageSection, styles.printsViewContainer)}>
            <div className={styles.toggleDiv}>
              <h5>Prints View</h5>
              <Toggle value={printsView === '1'} onToggle={handleChangePrintsViewFormValue} />
            </div>
            <p>Show print prices by default</p>
          </div>
        )}

        <div className={styles.buttonsContainer}>
          <button className='defaultButtonOutlined' onClick={handleCloseModal}>
            CANCEL
          </button>

          <button
            className={classNames('defaultButton', {
              ['defaultButtonDisabled']: isSubmitting || null,
            })}
            onClick={!isSubmitting ? onSubmit : undefined}
          >
            {(isSubmitting && 'SAVING...') || (collectionData ? 'UPDATE' : 'SUBMIT')}
          </button>
        </div>
      </form>
    </>
  )
}

export default CreateCollectionV2
